
import { Component, Prop, Mixins } from 'vue-property-decorator';
import { BAIconsMixin, FormRulesMixin, VuetifyMixin, StatusMixin, AuthMixin, SportsSelectorMixin, AthleteRoutingMixin } from '@/mixins';
import { AthleteProfileModel, UserModel } from '@/models';
import { SportModel } from '@/models/sport';
import { UserAccountModel } from '@/models/user/UserAccountModel';
import { Gender } from '@best-athletes/ba-types';
import TOSCheckbox from '@/components/forms/TOSCheckbox.vue';
import ProfilePictureUpload from '@/components/forms/ProfilePictureUpload.vue';
import { userApi } from '@/api/UserApi';
import { RoleName } from '@/../types';
import { setGAUserProperties } from '@/plugins/firebase';
import { sportStore, userStore } from '@/store';
import { SportName } from '@/../types/enums';
import { wait } from '@/helpers';

@Component({
    components: {
        TOSCheckbox,
        ProfilePictureUpload,
    }
})
export default class OnboardingDialog extends Mixins(BAIconsMixin, FormRulesMixin, VuetifyMixin, StatusMixin, AuthMixin, SportsSelectorMixin, AthleteRoutingMixin) {
    @Prop({type: Boolean}) value;
    @Prop({type: Boolean}) icon;
    @Prop() sportName: SportName;
    @Prop() nextUrl: string;
    Gender = Gender;

    newAthlete: AthleteProfileModel = new AthleteProfileModel();
    password: string = '';
    passwordConfirm: string = '';

	$refs:{ form: HTMLFormElement }

    availableSports: Array<SportName> = [SportName.Soccer];
    async created() {
        this.availableSports = this.SupportedSports;
        if( this.IsNotEmpty(this.sportName) ) this.availableSports = [this.sportName];
    }

    onError() {
        this.processing = false;
    }

    async createUser(): Promise<UserAccountModel> {
        this.setInfo( `Creating account for ${this.newAthlete.FullName}` );

        try{
            const user: UserModel = new UserModel().load({
                roles: [RoleName.Athlete, RoleName.FreeUser],
                email: this.newAthlete.email,
                email_verified: false,
                user_metadata: {
                    phone: this.newAthlete.PhoneNumber,
                    gender: this.newAthlete.gender,
                    tosAccepted: this.newAthlete.tosAgreed,                
                },
                pictureUrl: this.newAthlete.pictureUrl,
                firstName: this.newAthlete.firstName,
                lastName: this.newAthlete.lastName,
                password: this.password,
            })

            const newUser: UserAccountModel = await userApi.createUser(user);
            if( this.IsEmpty(newUser) ) {
                this.setError(`Error creating user ${user.email}`);
                throw( Error(`Unable to create New User: ${user.email}`));
            }
            return newUser;
        } catch(e) {
            throw(e);
        }
    }
    async createAthlete(): Promise<AthleteProfileModel> {
        this.setInfo( `Creating Athlete Profile for ${this.newAthlete.FullName}` );

        try {
            if( this.IsEmpty(this.newAthlete.athleteSports) ) {
                await sportStore.loadSports();
                
                var athleteSport: SportModel = sportStore.sports.find(sport => sport.name.includes(this.sportName));
                if( this.IsEmpty(athleteSport) ) {
                    // default to soccer
                    athleteSport = sportStore.sports.find(sport => sport.IsSoccer);
                }
                // add selected sport to the athlete sports
                if( this.IsNotEmpty(athleteSport?.id) ) {
                    this.newAthlete.athleteSports = [athleteSport.id];
                }
            }
            this.newAthlete.autocompleteDiscoverable = true;
            this.newAthlete.recruitingProfileComplete = true;
            this.newAthlete.nextUrl = this.nextUrl;

            const athlete: AthleteProfileModel = await userStore.createAthleteProfile({athlete: this.newAthlete});
            if( this.IsEmpty(athlete) ) {
                this.setError( `Error creating athlete ${this.newAthlete.Email}` );
                throw( Error(`Unable to create New Athlete: ${this.newAthlete.Email}`));
            }

            setGAUserProperties( {is_athlete: true} );
            this.setInfo( `Athlete Profile created for ${athlete.FullName}` );
            return athlete;
        } catch(e) {
            this.setError(`Error creating athlete: ${e}`);
            return undefined;
        }
    }

    processing: boolean = false;
    userExists: boolean = false;
    async submitForm() {      
        if( !this.$refs.form.validate() ) {
            this.setError( "Registration incomplete. Please complete all of the required fields" );
            return;
        }

        try {
            this.processing = true;
            const sportName = this.sportName;
            
            this.setInfo( `Creating user account` );
            const newUser: UserAccountModel = await this.createUser();
            if( this.IsEmpty(newUser) ) {
                this.setError("Unable to create user account. Please try again.");
                this.processing = false;
                return;
            }

            this.setInfo( `Account created. You will now be asked to log in.` );
            const auth0User = await this.loginAndSetUser(newUser);
            if( this.IsEmpty(auth0User) ) {
                this.setError("Login cancelled or failed");
                this.processing = false;
                return;
            }

            this.setInfo( `Retrieving account information...` );
            this.setInfo( `Initializing Athlete Profile for ${auth0User.given_name}` );
            this.sportName = sportName;
            const newAthlete: AthleteProfileModel = await this.createAthlete();
            if( this.IsEmpty(newAthlete) ) {
                this.setError("Athlete profile creation failed.");
                this.processing = false;
                return;
            }

            this.setInfo( `Created Athlete Profile`);
			await userStore.acceptTermsOfService( newAthlete.tosAgreed );
            this.setInfo( `Linking Athlete Profile for ${newAthlete.FullName}` );
			await userStore.useAthleteProfile( { profileId: newAthlete.id } );
            this.setInfo( `Welcome to Best Athletes, ${newAthlete.FullName}` );
            await wait(1000);
            this.$emit('complete');
        } catch(e) {
            if( e.message.includes('status code 500') ) {
                this.userExists = true;
                this.setError(`Unable to create user. User already exists.`);
            } else if( e.message.includes('Login required') ) {
                this.setError(`Login process failed. Please try again.`);
            } else {
                this.setError(`Error creating user: ${e}`);
            }
        }

        // close dialog
        this.processing = false;
    }
}

